import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search, Compass, Wrench, Infinity as InfinityIcon, CheckCircle2, CalendarDays, Mail } from "lucide-react";

const STEPS = [
  {
    icon: Search,
    label: "Step One",
    title: "Organizational Assessment",
    duration: "2 – 4 weeks",
    short: "Understand what's broken and why.",
    deliverables: [
      "A.L.I.G.N.™ Readiness Index across all five pillars",
      "Stakeholder interviews and document review",
      "Friction map: where alignment is breaking down today",
      "Executive findings deck with prioritized recommendations",
    ],
    outcome:
      "You walk away with a shared, evidence-based view of what is actually causing the drag — not opinions, not anecdotes.",
  },
  {
    icon: Compass,
    label: "Step Two",
    title: "Strategy & System Design",
    duration: "4 – 8 weeks",
    short: "Build the plan and the structure.",
    deliverables: [
      "Operating model and accountability map",
      "Workforce, leadership, and AI integration roadmap",
      "Governance rhythm: cadence, decision rights, KPIs",
      "Phased implementation plan with owners and timeline",
    ],
    outcome:
      "You leave with concrete, decision-ready systems — not slideware. Every recommendation maps to a measurable outcome.",
  },
  {
    icon: Wrench,
    label: "Step Three",
    title: "Implementation & Training",
    duration: "8 – 16 weeks",
    short: "Equip leaders and teams to execute.",
    deliverables: [
      "Leadership enablement and coaching",
      "Workforce program rollout (cohort-based where applicable)",
      "AI integration support — tooling, prompts, guardrails",
      "Embedded operating cadence with the leadership team",
    ],
    outcome:
      "We don't hand you a deck and disappear. We're in the room while the operating system goes live and the muscle memory takes hold.",
  },
  {
    icon: InfinityIcon,
    label: "Step Four",
    title: "Advisory & Retainer",
    duration: "Ongoing — quarterly or monthly",
    short: "Sustain and scale.",
    deliverables: [
      "Standing executive advisory cadence (CEO, COO, or board-level)",
      "Quarterly A.L.I.G.N. re-scoring and recalibration",
      "Crisis and inflection-point support on call",
      "Talent, leadership, and AI evolution — as your context changes",
    ],
    outcome:
      "An executive partnership that compounds. The first three steps create alignment; this step keeps it earned year over year.",
  },
];

export default function Process() {
  return (
    <main data-testid="page-process" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div
          className="absolute -top-32 -right-40 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(30,90,176,0.30), transparent 60%)" }}
        />
        <div
          className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(201,164,90,0.18), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pt-36 lg:pb-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow-light rise rise-1">How We Engage</div>
            <h1 className="font-display text-[44px] md:text-[68px] leading-[0.98] mt-6 rise rise-2">
              A Deliberate Path<br />
              <span className="text-[var(--gold-soft)]">From Diagnosis To Durability.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-[18px] leading-[1.55] text-[var(--navy-100)]/90 rise rise-3">
              Every engagement follows the same four-step rhythm. We can enter at any step, but we never skip
              the diagnosis. Alignment without diagnosis is just expensive guessing.
            </p>
          </div>
          <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-[var(--gold)]/20">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[var(--gold-soft)] font-semibold">
              At a Glance
            </div>
            <ul className="mt-6 space-y-3 text-[14px] leading-[1.55] text-[var(--navy-100)]/85">
              {[
                "Four-step engagement model",
                "Outcome-anchored, not deliverable-anchored",
                "Executive-grade documentation throughout",
                "Built for ongoing advisory partnership",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="mt-1 text-[var(--gold-soft)] flex-shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-[var(--bone)] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow">The Engagement</div>
          <h2 className="font-display text-[34px] md:text-[48px] leading-[1.05] text-[var(--navy-900)] mt-5 max-w-3xl">
            Four Steps. One Outcome. Alignment That Lasts.
          </h2>

          <div className="mt-16 space-y-10 lg:space-y-12">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isLast = i === STEPS.length - 1;
              return (
                <article
                  key={s.title}
                  data-testid={`process-step-${i}`}
                  className="grid lg:grid-cols-12 gap-8 lg:gap-12 relative"
                >
                  {/* Left — meta column */}
                  <div className="lg:col-span-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-sm bg-[var(--navy-950)] text-[var(--gold-soft)] flex items-center justify-center">
                        <Icon size={24} />
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] font-bold">
                          {s.label}
                        </div>
                        <div className="font-display text-[24px] leading-[1.1] text-[var(--navy-900)] mt-1">
                          {s.title}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[var(--navy-900)]/10 text-[10px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-semibold">
                      <CalendarDays size={12} className="text-[var(--gold)]" />
                      {s.duration}
                    </div>
                    <p className="mt-6 text-[14px] leading-[1.65] text-[var(--muted)] max-w-sm">
                      {s.short}
                    </p>
                  </div>

                  {/* Right — body */}
                  <div className="lg:col-span-8 bg-white border border-[var(--navy-900)]/8 p-8 lg:p-10">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] font-bold">
                      What you receive
                    </div>
                    <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                      {s.deliverables.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-2.5 text-[14px] leading-[1.55] text-[var(--navy-900)]"
                        >
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] flex-shrink-0"></span>
                          {d}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 hairline"></div>
                    <div className="mt-7 grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-6 items-start">
                      <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--navy-900)] font-bold">
                        Outcome
                      </div>
                      <p className="text-[15px] leading-[1.65] text-[var(--navy-900)] font-semibold">
                        {s.outcome}
                      </p>
                    </div>
                  </div>

                  {/* Vertical connector line — hidden on the last step + on mobile */}
                  {!isLast && (
                    <div className="hidden lg:block absolute left-7 top-[60px] w-px bg-[var(--gold)]/25" style={{ height: "calc(100% + 3rem)" }} />
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT'S DIFFERENT */}
      <section className="bg-[var(--cream)] py-20 lg:py-28" data-testid="process-difference">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">What's Different</div>
            <h2 className="font-display text-[34px] md:text-[44px] leading-[1.06] text-[var(--navy-900)] mt-5">
              We Sell Aligned Outcomes — Not Slideware.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-[var(--muted)] max-w-md">
              The traditional consulting model rewards thick decks and thin execution.
              We're built for the opposite. Here's how that shows up in our work.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                title: "Executive operators, not generalists",
                body: "Our team has sat in the seats we now advise. Fortune 100, federal, mission-driven.",
              },
              {
                title: "Outcome-anchored scopes",
                body: "Every engagement is structured around measurable alignment outcomes — not hours.",
              },
              {
                title: "Embedded, not extractive",
                body: "We work alongside your leadership team, not over them. Knowledge transfers naturally.",
              },
              {
                title: "Designed for durability",
                body: "Alignment doesn't stay aligned by itself. Step four is where the compounding starts.",
              },
            ].map((d) => (
              <div key={d.title} className="bg-white p-6 border border-[var(--navy-900)]/8">
                <div className="font-display text-[18px] leading-[1.2] text-[var(--navy-900)]">{d.title}</div>
                <p className="mt-3 text-[13px] leading-[1.65] text-[var(--muted)]">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-24 lg:py-28 relative overflow-hidden">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow-light">Start the Engagement</div>
          <h2 className="font-display text-[40px] md:text-[56px] leading-[1.04] mt-6">
            Begin With The Diagnostic.<br />
            <span className="text-[var(--gold-soft)]">End With Alignment That Holds.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-[16px] leading-[1.6] text-[var(--navy-100)]/85">
            The A.L.I.G.N.™ Readiness Index is the entry point for Step One. It's a five-minute diagnostic
            that returns an executive-grade report — and it's the same instrument we use inside every engagement.
          </p>
          <div className="mt-12 flex flex-wrap justify-center items-center gap-4">
            <Link
              to="/assessment"
              data-testid="process-cta-assessment"
              className="btn-gold px-8 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2"
            >
              Take the Diagnostic <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="process-cta-contact"
              className="px-8 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2 border border-[var(--gold)]/40 text-[var(--bone)] hover:bg-[var(--bone)]/5 transition-colors"
            >
              <Mail size={14} /> Talk to the Team <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
